// install: pnpm add @solid-primitives/media
import { createMediaQuery } from '@solid-primitives/media'

/**
 * PandaCSS default breakpoints (px)
 * Keep in sync with your panda.config.ts -> theme.breakpoints
 */
const bp = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const

// helpers to build exact ranges
const atLeast = (min: string) => `(min-width: ${min})`
const down    = (next: string) => `(max-width: calc(${next} - 0.02px))` // avoid overlap
const between = (min: string, next: string) => `${atLeast(min)} and ${down(next)}`

/**
 * Media query strings matching Panda's semantics:
 * - "<bp>": min-width at that breakpoint
 * - "<bp>Only": >= bp and < next bp
 * - "<bp>Down": < next bp
 * - "XToY": >= X and < Y
 */
export const mq = {
  sm: atLeast(bp.sm),
  smOnly: between(bp.sm, bp.md),
  smDown: down(bp.md),

  md: atLeast(bp.md),
  mdOnly: between(bp.md, bp.lg),
  mdDown: down(bp.lg),

  lg: atLeast(bp.lg),
  lgOnly: between(bp.lg, bp.xl),
  lgDown: down(bp.xl),

  xl: atLeast(bp.xl),
  xlOnly: between(bp.xl, bp['2xl']),
  xlDown: down(bp['2xl']),

  '2xl': atLeast(bp['2xl']),
  '2xlOnly': atLeast(bp['2xl']),          // no upper bound after 2xl
  '2xlDown': down(bp['2xl']),             // < 2xl (alias of xlDown semantics)

  smToMd: between(bp.sm, bp.md),
  smToLg: between(bp.sm, bp.lg),
  smToXl: between(bp.sm, bp.xl),
  smTo2xl: between(bp.sm, bp['2xl']),

  mdToLg: between(bp.md, bp.lg),
  mdToXl: between(bp.md, bp.xl),
  mdTo2xl: between(bp.md, bp['2xl']),

  lgToXl: between(bp.lg, bp.xl),
  lgTo2xl: between(bp.lg, bp['2xl']),

  xlTo2xl: between(bp.xl, bp['2xl']),
} as const

/**
 * Reactive signals (Solid) for every query above.
 * Usage:
 *   import { when } from '@/path'
 *   createEffect(() => { if (when.md()) { ... } })
 *   createEffect(() => { if (when['xlTo2xl']()) { ... } })
 */
export const when = Object.fromEntries(
  Object.entries(mq).map(([k, q]) => [k, createMediaQuery(q)])
) as Record<keyof typeof mq, () => boolean>

// Common aliases if you like the "Up" naming:
export const smUp  = when.sm
export const mdUp  = when.md
export const lgUp  = when.lg
export const xlUp  = when.xl
export const xxlUp = when['2xl']
